import { NavLink } from 'react-router-dom';
import { cn } from '@/shared/lib/utils';
import { usePermissions } from '@/shared/hooks/usePermissions';
import type { Permission } from '@/shared/types/permissions';
import {
  InboxIcon,
  ChatIcon,
  TargetIcon,
  DigestIcon,
  FolderIcon,
  SettingsIcon,
  LogoIcon,
  MegaphoneIcon,
  EditIcon,
  ClockIcon,
  UsersIcon,
} from '@/shared/components/icons';

interface NavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  /** Permission required to see this nav item (UI convenience only) */
  permission?: Permission;
  /** If true, only match when the path is exactly equal */
  end?: boolean;
}

/**
 * Navigation items with optional permission requirements.
 * Items without permission requirements are visible to all authenticated users.
 */
const navItems: NavItem[] = [
  // Директолог (спека 0139) — первыми: это теперь главный экран.
  { to: '/audit', icon: MegaphoneIcon, label: 'Аудит' },
  { to: '/actions', icon: EditIcon, label: 'Правки', permission: 'view_analytics' },
  { to: '/journal', icon: ClockIcon, label: 'Журнал', permission: 'view_analytics' },
  { to: '/agency', icon: UsersIcon, label: 'Агентство' },
  { to: '/inbox', icon: InboxIcon, label: 'Входящие' },
  { to: '/chat', icon: ChatIcon, label: 'Чат', permission: 'use_chat' },
  { to: '/goals', icon: TargetIcon, label: 'Цели' },
  { to: '/projects', icon: FolderIcon, label: 'Проекты' },
  { to: '/digest', icon: DigestIcon, label: 'Дайджесты', permission: 'manage_digests' },
  { to: '/settings', icon: SettingsIcon, label: 'Настройки' },
];

export function Sidebar() {
  const { can } = usePermissions();

  // Filter nav items based on permissions
  const visibleItems = navItems.filter((item) => {
    // No permission required - show to all authenticated users
    if (!item.permission) {
      return true;
    }
    // Check permission
    return can(item.permission);
  });

  return (
    <aside className="hidden w-64 flex-shrink-0 flex-col border-r border-border bg-card lg:flex">
      {/* Logo */}
      <div className="flex h-16 items-center gap-2 px-6 border-b border-border">
        <LogoIcon className="h-8 w-8 text-foreground" />
        <span className="text-xl font-semibold tracking-tight text-foreground">Mira</span>
      </div>

      {/* Navigation */}
      <nav className="mt-4 flex-1 space-y-1 px-3">
        {visibleItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              )
            }
          >
            {({ isActive }) => (
              <>
                <item.icon
                  className={cn(
                    'h-4 w-4',
                    isActive ? 'text-foreground' : 'text-muted-foreground'
                  )}
                />
                {item.label}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}
